/* eslint-disable jsx-a11y/no-autofocus */
//! TODO: We need to move away from using `autoFocus` for these modals, as it can cause issues with focus management.
import { __assign, __rest } from "tslib";
import React, { useEffect, useCallback, useRef } from 'react';
import * as Styles from './modal.styled';
import { Button } from '@at/design-system-elements';
import { conditionalClassList } from '@at/formatter-utils/lib/class-name-utils/class-name.util';
import { ModalPortal } from './portal';
import FocusLock from 'react-focus-lock';
import { IconArrowLeft, IconClose } from '@at/design-system-icons';
import { StyledVisuallyHiddenContainer } from '@at/design-system-foundation';
var escapeKeyPressed = function (event) {
    return event.key ? event.key === 'Escape' : event.keyCode === 27;
};
var ModalElement = function (_a) {
    var _b, _c;
    var attributes = _a.attributes, additionalClass = _a.additionalClass, isShowing = _a.isShowing, children = _a.children, _d = _a.className, className = _d === void 0 ? '' : _d, modalTitle = _a.modalTitle, closeText = _a.closeText, hasBackLink = _a.hasBackLink, footerButtonText = _a.footerButtonText, footerButtonTestId = _a.footerButtonTestId, modalHashName = _a.modalHashName, ariaLabelledBy = _a.ariaLabelledBy, ariaDescribedBy = _a.ariaDescribedBy, hideFn = _a.hideFn, variation = _a.variation, overlayName = _a.overlayName, _e = _a.shouldTrackOnOpen, shouldTrackOnOpen = _e === void 0 ? true : _e, _f = _a.disableEscButton, disableEscButton = _f === void 0 ? false : _f, _g = _a.disableFocusTrap, disableFocusTrap = _g === void 0 ? false : _g, _h = _a.disableClosingButton, disableClosingButton = _h === void 0 ? false : _h, _j = _a.disableOutsideModalCloseClick, disableOutsideModalCloseClick = _j === void 0 ? false : _j, trackingCallback = _a.trackingCallback, otherHTMLProperties = __rest(_a, ["attributes", "additionalClass", "isShowing", "children", "className", "modalTitle", "closeText", "hasBackLink", "footerButtonText", "footerButtonTestId", "modalHashName", "ariaLabelledBy", "ariaDescribedBy", "hideFn", "variation", "overlayName", "shouldTrackOnOpen", "disableEscButton", "disableFocusTrap", "disableClosingButton", "disableOutsideModalCloseClick", "trackingCallback"]);
    var ariaLabelledByWithFallback = (_b = otherHTMLProperties['aria-labelledby']) !== null && _b !== void 0 ? _b : ariaLabelledBy;
    var ariaDescribedByWithFallback = (_c = otherHTMLProperties['aria-describedby']) !== null && _c !== void 0 ? _c : ariaDescribedBy;
    var baseClassName = 'atds-modal';
    var classNameList = conditionalClassList(baseClassName, !!hasBackLink && "".concat(hasBackLink && "".concat(baseClassName, "--backlink ")), !!additionalClass && additionalClass, !!variation && "".concat(baseClassName, "--").concat(variation), className);
    var modalOverlayRef = useRef(null);
    var handleTracking = useCallback(function (isModalOpen) {
        if (!trackingCallback)
            return;
        var modalTrackingLabel = isModalOpen ? 'open' : 'close';
        trackingCallback({
            modalAction: modalTrackingLabel,
            overlayContextEntity: overlayName
                ? {
                    modalVariation: variation,
                    overlayContextName: overlayName,
                }
                : undefined,
        });
    }, [trackingCallback, overlayName, variation]);
    useEffect(function () {
        if (isShowing && shouldTrackOnOpen) {
            handleTracking(true);
        }
    }, [handleTracking, isShowing, shouldTrackOnOpen]);
    useEffect(function () {
        var onKeyDown = function (event) {
            if (escapeKeyPressed(event) && isShowing && !disableEscButton) {
                hideFn();
            }
        };
        if (isShowing) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', onKeyDown, false);
        }
        return function () {
            document.body.style.overflow = 'unset';
            document.removeEventListener('keydown', onKeyDown, false);
        };
    }, [isShowing, hideFn, disableEscButton, disableOutsideModalCloseClick, handleTracking]);
    if (!isShowing) {
        return null;
    }
    function onOverlayClick(e) {
        if (e.target === modalOverlayRef.current && !disableOutsideModalCloseClick) {
            hideFn();
        }
    }
    var drawerHeaderButton = (React.createElement(Styles.DrawerActionButton, __assign({ onClick: function () {
            hideFn();
            handleTracking(false);
        }, autoFocus: true, "aria-label": "Close", className: "atds-modal--close atds-type-fiesta" }, attributes),
        React.createElement(Styles.ActionIcon, null,
            React.createElement(IconClose, { title: '', customSize: { width: 14, height: 14 }, viewBox: "0 0 16 16" })),
        React.createElement("p", null, "Close")));
    var defaultHeaderButton = (React.createElement(Styles.ActionButton, __assign({ onClick: function () {
            hideFn();
            handleTracking(false);
        }, autoFocus: true, "aria-label": "Close", className: "atds-modal--close atds-type-fiesta", hasBackLink: hasBackLink }, attributes),
        hasBackLink ? (React.createElement(Styles.ActionIcon, null,
            React.createElement(IconClose, { title: '', customSize: { width: 14, height: 14 }, viewBox: "0 0 16 16" }))) : (React.createElement(Styles.ActionIcon, null,
            React.createElement(IconArrowLeft, { title: '', customSize: { width: 22, height: 22 }, viewBox: "0 0 24 24" }))),
        React.createElement("span", null, closeText ? closeText : 'Go back')));
    var isDrawer = variation === 'drawer';
    var headerButton = isDrawer && !disableClosingButton
        ? drawerHeaderButton
        : !isDrawer && !disableClosingButton
            ? defaultHeaderButton
            : null;
    var footerButton = (React.createElement(Button, { className: "atds-modal--close footer-button", text: footerButtonText, "data-testid": footerButtonTestId !== null && footerButtonTestId !== void 0 ? footerButtonTestId : '', onInteraction: function () {
            hideFn();
            handleTracking(false);
        } }));
    return (React.createElement(FocusLock, { returnFocus: false, autoFocus: false, disabled: disableFocusTrap },
        React.createElement(Styles.ModalOverlay, { id: modalHashName, role: "dialog", className: classNameList, "aria-labelledby": ariaLabelledByWithFallback, "aria-describedby": ariaDescribedByWithFallback, "aria-modal": "true", onClick: onOverlayClick, ref: modalOverlayRef },
            React.createElement(Styles.Modal, __assign({ role: "document", variation: variation }, otherHTMLProperties),
                headerButton && React.createElement(Styles.ModalHeader, null, headerButton),
                React.createElement(Styles.ModalBody, { variation: variation },
                    React.createElement(Styles.ModalContent, null,
                        modalTitle && (React.createElement(StyledVisuallyHiddenContainer, { as: "h2" }, modalTitle)),
                        children)),
                footerButtonText && React.createElement(Styles.ModalFooter, null, footerButton)))));
};
export var Modal = function (props) {
    var isShowing = props.isShowing, _a = props.portal, portal = _a === void 0 ? false : _a;
    if (portal) {
        return (React.createElement(ModalPortal, { isShowing: isShowing },
            React.createElement(ModalElement, __assign({}, props))));
    }
    else {
        return React.createElement(ModalElement, __assign({}, props));
    }
};
export default Modal;
